// 티어 + 레벨 계산 — 도메인 코어 정본 (웨이브1a 이동: src/lib/tiers.ts → 여기, 2026-07-03)
// 황소 진화 7단계. 구간별 필요 EXP = 50 * (level^1.6) 누적.
// EXP 엔진(exp.ts)이 expToLevel로 레벨 산출. user_levels 갱신에 사용.
// 웹 lib/tiers.js 는 동결 미러 — 규칙 변경 시 이 파일이 정본, 웹 제거는 웨이브1b (design doc §4.1).

export type Tier = {
  key: string;
  range: [number, number];
  color: string;
  bullStage: number;
  bullKey: string;
  label: string;
};

export const TIERS: Tier[] = [
  { key: "SEED",    range: [1, 5],   color: "#555555",  bullStage: 0, bullKey: "calf",  label: "알등이" },
  { key: "SCOUT",   range: [6, 10],  color: "#00cc44",  bullStage: 1, bullKey: "young", label: "송아지" },
  { key: "ANALYST", range: [11, 18], color: "#4488ff",  bullStage: 2, bullKey: "young", label: "어린 황소" },
  { key: "TRADER",  range: [19, 27], color: "#ff6600",  bullStage: 3, bullKey: "bull",  label: "황소" },
  { key: "ALPHA",   range: [28, 36], color: "#aa44ff",  bullStage: 4, bullKey: "black", label: "근육 황소" },
  { key: "ORACLE",  range: [37, 44], color: "#ffcc00",  bullStage: 5, bullKey: "gold",  label: "금빛 황소" },
  { key: "LEGEND",  range: [45, 50], color: "gradient", bullStage: 6, bullKey: "gold",  label: "전설의 황소" },
];

export const MAX_LEVEL = 50;

/** 레벨 → 티어 객체 */
export function tierOf(level: number): Tier {
  return TIERS.find((t) => level >= t.range[0] && level <= t.range[1]) || TIERS[0];
}

/** level → bullKey 바로 변환 (UserBadge 렌더용) */
export function bullKeyOf(level: number): string {
  return (tierOf(level) || TIERS[0]).bullKey;
}

/** level N → N+1 로 가기 위해 필요한 EXP */
export function expForLevel(level: number): number {
  if (level < 1) return 0;
  return Math.floor(50 * Math.pow(level, 1.6));
}

/** 누적 EXP → 레벨 + 진행률 */
export function expToLevel(totalExp: number): {
  level: number; currentLevelExp: number; nextLevelExp: number; progress: number;
} {
  let exp = totalExp;
  let level = 1;
  while (level < MAX_LEVEL) {
    const need = expForLevel(level);
    if (exp < need) break;
    exp -= need;
    level += 1;
  }
  const need = level < MAX_LEVEL ? expForLevel(level) : 1;
  return {
    level,
    currentLevelExp: exp,
    nextLevelExp: need,
    progress: Math.min(1, exp / need),
  };
}

/** 누적 필요 EXP (Lv1 → N) */
export function cumulativeExpForLevel(targetLevel: number): number {
  let sum = 0;
  for (let i = 1; i < targetLevel; i++) sum += expForLevel(i);
  return sum;
}

/** 레벨 메탈 6단 — 제품 아키텍처 v2 Q4 확정(2026-07-11). 신규 색 토큰 0: 무채 파생 + gold.
 *  SEED=iron · SCOUT/ANALYST=steel(2티어 공유) · TRADER=bronze · ALPHA=silver · ORACLE=gold · LEGEND=gold+발광 */
export type Metal = "iron" | "steel" | "bronze" | "silver" | "gold" | "legend";
const METAL_BY_TIER: Record<string, Metal> = {
  SEED: "iron", SCOUT: "steel", ANALYST: "steel", TRADER: "bronze", ALPHA: "silver", ORACLE: "gold", LEGEND: "legend",
};
/** level → 메탈. 범위 밖(0·음수)은 tierOf 폴백대로 iron */
export function metalOf(level: number): Metal {
  return METAL_BY_TIER[tierOf(level).key] || "iron";
}
